import { FC } from 'react';
import classNames from 'classnames';
import { Table } from '@lambdacurry/component-library';
import { medusaPriceAmountToMoney } from 'lib/home/util/currency';
import { selectExtraLineItems, selectPremiumLineItems, selectSubscriptionItem } from 'lib/home/util/products';
import { MedusaCart, SubscriptionFrequency } from 'lib/home/generated/types/api';
import { SUBSCRIPTION_FREQUENCIES } from '../../../util/constants';
import { current } from 'immer';
import { parseVariantMetadata } from '../../../../../api-store/src/helpers/metadata';

/* TODO: Correct the typecasting in Medusa Admin for `Discount`, `Region`, etc. so that nullable fields are correct */
export interface CartPriceBreakdownProps {
  cart: MedusaCart;
  className?: string;
  subscriptionFrequency: SubscriptionFrequency;
}

export const CartPriceBreakdown: FC<CartPriceBreakdownProps> = ({ cart, className, subscriptionFrequency }) => {
  const discounts = cart?.discounts;
  const discount_total = cart?.discount_total;
  const gift_cards = cart?.gift_cards;
  const gift_card_total = cart?.gift_card_total;
  const items = cart?.items;
  const subscriptionItem = selectSubscriptionItem(items);
  const extraCartItems = selectExtraLineItems(items);
  const premiumCartItems = selectPremiumLineItems(items);
  const premiumTotal = premiumCartItems.reduce(
    (prev, current) =>
      parseVariantMetadata(current.variant.metadata).premium_upcharge
        ? parseVariantMetadata(current.variant.metadata).premium_upcharge + prev
        : prev,
    0
  );

  return (
    <Table className={classNames(`cart-price-breakdown`, className)}>
      <tbody>
        {subscriptionItem && (
          <tr>
            <td>
              <b>
                {subscriptionItem.title} /{' '}
                {subscriptionFrequency === SubscriptionFrequency.OneWeek
                  ? 'week'
                  : `${SUBSCRIPTION_FREQUENCIES[subscriptionFrequency]} weeks`}
              </b>
            </td>
            <td>{medusaPriceAmountToMoney(subscriptionItem.unit_price)}</td>
          </tr>
        )}

        {premiumCartItems.length > 0 && (
          <tr>
            <td>Premium Meals</td>
            <td>+{medusaPriceAmountToMoney(premiumTotal)}</td>
          </tr>
        )}

        {extraCartItems.map((item, index) => (
          <tr key={item.id}>
            <td>
              +{item.quantity} {item.variant.product.title}
            </td>
            <td>{medusaPriceAmountToMoney(item.quantity * item.unit_price)}</td>
          </tr>
        ))}

        {!!discount_total && (
          <tr className="font-bold text-brand-cool-green-500">
            <td>Discount:</td>
            <td>-{medusaPriceAmountToMoney(cart?.discount_total)}</td>
          </tr>
        )}

        {discounts &&
          discounts.map(discount => (
            <tr className="text-brand-cool-green-500" key={discount.id}>
              <td className="!text-sm" data-cy="applied-discount-code">
                {discount.code}
              </td>
              <td className="!text-sm">
                {discount.rule.type === 'PERCENTAGE' && <>{discount.rule.value}%</>}
                {discount.rule.type === 'FIXED' && <>{medusaPriceAmountToMoney(discount.rule.value)}</>}
              </td>
            </tr>
          ))}

        {!!gift_card_total && (
          <tr className="font-bold text-brand-cool-green-500">
            <td>Gift Cards:</td>
            <td>-{medusaPriceAmountToMoney(cart?.gift_card_total)}</td>
          </tr>
        )}

        {gift_cards &&
          gift_cards
            .filter(card => card.balance > 0)
            .map(card => (
              <tr className="text-brand-cool-green-500" key={card.id}>
                <td className="!text-sm" data-cy="applied-discount-code">
                  {card.code}
                </td>
                <td className="!text-sm">{medusaPriceAmountToMoney(card.balance)}</td>
              </tr>
            ))}
      </tbody>
      <tbody>
        <tr>
          <td>Your Total:</td>
          <td>{medusaPriceAmountToMoney(cart?.subtotal)}</td>
        </tr>
        <tr>
          <td className="!text-sm">Shipping</td>
          <td className="!text-sm">{medusaPriceAmountToMoney(cart?.shipping_total)}</td>
        </tr>
        <tr>
          <td className="!text-sm">Estimated Tax:</td>
          <td className="!text-sm">{medusaPriceAmountToMoney(cart?.tax_total)}</td>
        </tr>
      </tbody>
      <tbody>
        <tr>
          <td className="font-bold">Total:</td>
          <td className="font-bold text-brand-cool-green-500">{medusaPriceAmountToMoney(cart?.total)}</td>
        </tr>
      </tbody>
    </Table>
  );
};
